import {
  Box,
  Divider
} from '@mui/material'

import React, { useEffect, useState, useContext } from 'react'
import { type Log, type Option, type CombatStats } from 'src/types'

// context
import { SaveDataContext, SaveDispatchContext } from 'src/store/save/Store'
import { StatsDataContext, StatsDispatchContext } from 'src/store/stats/Store'
import { FightDataContext } from 'src/store/fights/Store'
import { NPCDataContext } from 'src/store/npcs/Store'
import { LogDispatchContext } from 'src/store/logs/Store'
import { InventoryDispatchContext } from 'src/store/inventory/Store'
import { NavigationDataContext, NavigationDispatchContext } from 'src/store/navigation/Store'

// actions
import { setCounter, setVariable } from 'src/store/save/actions'
import { setPlayerStat } from 'src/store/stats/actions'
import { addLog, addLogs, deleteLogs } from 'src/store/logs/actions'
import { setSwitch, setSwitches } from 'src/store/navigation/actions'
import { resetQuantity, setQuantity } from 'src/store/inventory/actions'

// hooks
import useSharedStore from 'src/hooks/useSharedStore'

// components
import Logs from 'src/components/common/Logs'
import Bar from 'src/components/common/Bar'
import Loot from 'src/components/common/Loot'
import Bag from 'src/components/common/Bag'
import CarryCapacity from 'src/components/common/CarryCapacity'
import Sprite from 'src/components/common/Sprite'
import Cue from 'src/components/common/Cue'
import Options from 'src/components/common/Options'

// utils
import { createEnemy, calculateDamage, challengeRoll, rollDice } from 'src/utils/combat'

// TODO: replace this with options from saveData context
import { combatOptions, victoryOptions, defeatOptions, leave } from 'src/data/fixtures/actions'


const containerStyle = {
  zIndex: 4,
  position: 'absolute',
  top: '64px',
  bottom: '64px',
  width: '100%',
  height: 'calc(100% - 124px)',
  backgroundColor: 'black',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'space-between',
  paddingX: '24px'
}

const enemySpriteContainerStyle = {
  position: 'relative',
  maxWidth: '100%',
  margin: 'auto',
  backgroundColor: 'black',
  height: '184px',
  maxHeight: '184px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const Combat = () => {
  const { save } = useContext(SaveDataContext)
  const { stats } = useContext(StatsDataContext)
  const { dispatchStats } = useContext(StatsDispatchContext)
  const { dispatchSave } = useContext(SaveDispatchContext)
  const { dispatchLogs } = useContext(LogDispatchContext)
  const { navigation } = useContext(NavigationDataContext)
  const { dispatchNavigation } = useContext(NavigationDispatchContext)
  const { dispatchInventory } = useContext(InventoryDispatchContext)
  const { fights } = useContext(FightDataContext)
  const { NPCs } = useContext(NPCDataContext)

  // sharedStoreActions
  const { processEffects } = useSharedStore()

  // enemy stats
  const [enemy, setEnemy] = useState<CombatStats>()
  const [enemyHP, setEnemyHP] = useState<number>()
  const [enemyAP, setEnemyAP] = useState<number>()

  // combat flow
  // const [onCooldown] = useState(false) // placeholder for real-time battle
  const [turnDelayMS] = useState(200)
  const [isPlayerDefending, setPlayerDefending] = useState(false)
  const [isPlayerTurn, setPlayerTurn] = useState(true)

  // === UI settings === //
  const [showEnemySprites] = useState(true)
  const [showEnemyHP] = useState(true)
  const [cue, setCue] = useState<Log>({ text: '' })

  // actions
  const [options, setOptions] = useState(combatOptions)

  // === ENEMY SETUP === //
  // enemy is created when fight starts
  const fightID = save.variables.fight_id
  const fight = fights[fightID]
  
  if (!fight) {
    throw new Error(`Combat Error: Fight ${fightID} not found`)
  }
  
  const { npcId } = fight
  const NPC = NPCs[npcId]

  // enemy states
  const [enemyDodged, setEnemyDodged] = useState(false)
  const [enemyDamaged, setEnemyDamaged] = useState(false)

  useEffect(() => {
    const newEnemy: CombatStats = createEnemy(NPC, fight.lvl)
    setEnemy(newEnemy)
    setEnemyHP(newEnemy.hp_max)
    setEnemyAP(newEnemy.ap_max)
    
    if (fight.lootEffects?.items) {
      Object.keys(fight.lootEffects.items).forEach(itemID => {
        // @ts-expect-error
        const itemEffect = fight.lootEffects.items[itemID]
        setQuantity(dispatchInventory, 'loot', itemID, itemEffect)
      })
    }

    if (fight.logs?.length) {
      addLogs(dispatchLogs, fight.logs.slice(0, -1))
      const openingLog: Log = fight.logs[fight.logs.length - 1]
      setCue(openingLog)
    } else {
      const openingLog: Log = { text: `The ${newEnemy.id} stands before you, ready to strike.` }
      setCue(openingLog)
    }

    return () => {
      // resets Combat Navigation
      setSwitches(dispatchNavigation, [
        { enemy_ko: false },
        { enemy_spared: false },
        { enemy_dead: false },
        { loot_open: false }
      ])
      // resets Combat Loot
      resetQuantity(dispatchInventory, 'loot')

      // resets Combat Logs
      deleteLogs(dispatchLogs)
    }
  }, [])

  // === PLAYER SETUP === //
  // setting the player
  const player: CombatStats = {
    id: 'player',
    lvl: stats.player.player_lvl,
    hp: stats.total.player_hp,
    hp_max: stats.total.player_hp_max,
    ap: stats.total.player_ap,
    ap_max: stats.total.player_ap_max,
    vit: stats.total.player_vit,
    str: stats.total.player_str,
    dex: stats.total.player_dex,
    karma: stats.total.player_karma
  }

  // mimicking the enemy setPlayerState for consistency
  // player stats are in the store
  const playerHP = stats.total.player_hp
  const playerAP = stats.total.player_ap
  const setPlayerAP = (AP: number) => { setPlayerStat(dispatchStats, 'player_ap', { '=': AP }) }
  const setPlayerHP = (HP: number) => { setPlayerStat(dispatchStats, 'player_hp', { '=': HP }) }

  // enemyTurn
  useEffect(() => {
    if (!isPlayerTurn) {
      if (enemy && enemyHP !== undefined && enemyHP > 0) {
        const damage = calculateDamage(enemy, player, isPlayerDefending)
        const dodged = rollDice(1, 20) + enemy.dex < 5 + player.dex 

        if (dodged) {
          const log: Log = {
            text: 'You dodged the attack',
            omen: 'good'
          }
          addLog(dispatchLogs, log)
        } else {
          setPlayerHP(playerHP - damage < 0 ? 0 : playerHP - damage)
          if (enemyAP !== undefined) {
            setEnemyAP(enemyAP > 0 ? enemyAP - 1 : 0)
          }
          const log: Log = {
            text: `You lost ${damage} hp`,
            omen: 'bad'
          }
          addLog(dispatchLogs, log)
        }

        setEnemyDamaged(false)
        setEnemyDodged(false)
        setPlayerTurn(true)
      }
    }
  }, [isPlayerTurn])

  // process battle end
  useEffect(() => {
    if (enemy && enemyHP !== undefined && enemyHP < 1) {
      if (fight.victoryEffects) {
        processEffects(fight.victoryEffects)
      }
      setCounter(dispatchSave, 'player_victories', { '+': 1 })
      const log: Log = {
        text: `you knocked the ${enemy.id} out!`
      }
      addLog(dispatchLogs, log)
      setOptions(victoryOptions)
      setSwitch(dispatchNavigation, 'enemy_ko', true)
    }
  }, [enemyHP])

  useEffect(() => {
    if (playerHP < 1) {
      const gameOverLog: Log = {
        text: 'You were knocked out',
        omen: 'bad'
      }

      addLog(dispatchLogs, gameOverLog)
      setCounter(dispatchSave, 'player_defeats', { '+': 1 })
      setOptions(defeatOptions)
    }
  }, [playerHP])

  const handleOption = ({ text, effects }: Option) => {
    // clears the opening cue
    if (cue) {
      addLog(dispatchLogs, cue)
      setCue({ text: '' })
    }
    if (enemy) {
      if (text === 'attack') {
        handleAttackClick()
        processEffects(effects)
        const strExp = calculateExp(enemy.lvl, stats.player.player_str)
        const dexExp = calculateExp(enemy.lvl, stats.player.player_dex)
        setPlayerStat(dispatchStats, 'player_str_exp', { '+': strExp * 1 })
        setPlayerStat(dispatchStats, 'player_dex_exp', { '+': dexExp * 0.5 })
      } else if (text === 'defend') {
        handleDefendClick()
        processEffects(effects)
        const vitExp = calculateExp(enemy.lvl, stats.player.player_vit)
        const dexExp = calculateExp(enemy.lvl, stats.player.player_dex)
        setPlayerStat(dispatchStats, 'player_vit_exp', { '+': vitExp * 1 })
        setPlayerStat(dispatchStats, 'player_dex_exp', { '+': dexExp * 0.5 })
      } else if (text === 'run') {
        processEffects(effects)
        const dexExp = calculateExp(enemy.lvl, stats.player.player_dex)
        setPlayerStat(dispatchStats, 'player_dex_exp', { '+': dexExp * 1 })
        const escaped = challengeRoll(10 + enemy.dex, player.dex)
        if (escaped) {
          setSwitch(dispatchNavigation, 'combat_open', false)
        } else {
          const failedEscapeLog: Log = {
            text: `you couldn't outrun the ${enemy.id}`,
            omen: 'bad'
          }
          addLog(dispatchLogs, failedEscapeLog)
        }
      } else if (text === 'loot') {
        processEffects(effects)
        setSwitch(dispatchNavigation, 'loot_open', true)
      } else if (text === 'kill') {
        const killLog: Log = {
          text: `You took one final look at the ${enemy.id} and claimed its life.`,
          omen: 'bad'
        }
        processEffects(effects)
        if (fight.killEffects) {
          processEffects(fight.killEffects)
        }
        addLog(dispatchLogs, killLog)
        setSwitches(dispatchNavigation, [
          { loot_open: false },
          { enemy_dead: true }
        ])
        setOptions([leave])
      } else if (text === 'spare') {
        const spareLog: Log = {
          text: `You had mercy for the ${enemy.id} and spared its life.`,
          omen: 'good'
        }
        processEffects(effects)
        if (fight.spareEffects) {
          processEffects(fight.spareEffects)
        }
        addLog(dispatchLogs, spareLog)
        setSwitches(dispatchNavigation, [
          { loot_open: false },
          { enemy_spared: true }
        ])
        setOptions([leave])
      } else if (text === 'leave') {
        processEffects(effects)
        setSwitch(dispatchNavigation, 'combat_open', false)
        // Clear the fight_id when leaving combat
        setVariable(dispatchSave, 'fight_id', '')
      } else if (text === 'let go') {
        processEffects(effects)
        setSwitches(dispatchNavigation, [
          { game_over: true },
          { combat_open: false }
        ])
      }

      setTimeout(() => {
        setPlayerTurn(false)
      }, turnDelayMS)
    }
  }

  const handleDefaultOption = () => { }

  const handleDefendClick = () => {
    setPlayerDefending(true)
    const defendingLog: Log = { text: 'You braced yourself for impact' }
    addLog(dispatchLogs, defendingLog)
  }

  const handleAttackClick = () => {
    if (enemy && enemyHP) {
      setPlayerAP(playerAP > 0 ? playerAP - 1 : 0)
      const damage = calculateDamage(player, enemy)
      
      const missed = rollDice(1, 20) + player.dex < 5 + enemy.dex 

      if (missed) {
        const attackingLog: Log = {
          text: `${enemy.id} dodged your attack!`
        }
        setEnemyDodged(true)
        addLog(dispatchLogs, attackingLog)
      } else {
        setEnemyHP(enemyHP - damage < 0 ? 0 : enemyHP - damage)
        
        const attackingLog: Log = {
          text: `${enemy.id} lost ${damage} hp`
        }

        setEnemyDamaged(true)
        addLog(dispatchLogs, attackingLog)
      }
    }
  }

  const calculateExp = (enemyLvl: number, playerLvl: number) => {
    const expLvlMultiplier = enemyLvl / playerLvl
    return 10 * expLvlMultiplier
  }

  // development build logs
  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Combat mounted') }
    return process.env.REACT_APP_BENCHMARK === 'true' ? () => console.log('Combat unmounted') : () => { }
  }, [])

  useEffect(() => {
    if (process.env.REACT_APP_BENCHMARK === 'true') { console.log('Combat rendered') }
  })

  // Clear fight_id when combat closes
  useEffect(() => {
    if (!navigation.combat_open && save.variables?.fight_id) {
      setVariable(dispatchSave, 'fight_id', '')
    }
  }, [navigation.combat_open, save.variables?.fight_id, dispatchSave])

  return !enemy
    ? (
      <Box sx={{ ...containerStyle, backgroundColor: 'black' }} />
      )
    : (
      <Box sx={containerStyle} className="combat">
        { navigation.loot_open ? (
          <Box>
            <Loot />
            <Divider sx={{ marginY: 2 }} />
            <Bag />
            <Divider sx={{ marginY: 2 }} />
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center'}} className="carry-capacity-wrapper">
              <Box sx={{ maxWidth: '100%' }}>
                <CarryCapacity />
              </Box>
            </Box>
            
          </Box>
        ) : null }
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'center' }} className='enemyStats'>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%'}}>
              { !navigation.loot_open && !navigation.enemy_dead && showEnemyHP && enemy && enemyHP ? (
                <Bar currentValue={enemyHP} maximumValue={enemy.hp_max} hideValues label={enemy.id.toUpperCase()} color='crimson'/>
              )
                : null}
            </Box>
          </Box>
          { !navigation.loot_open && !navigation.enemy_dead && showEnemySprites && enemy && enemyHP !== undefined
            ? (
              <Box sx={enemySpriteContainerStyle}>
                <div style={{position: 'relative', height: '128px', width: '128px', alignSelf: 'flex-end', display: 'flex', justifyContent: 'center', alignItems: 'center', transform: `scale(${128 / 32})`}} className={enemyDodged ? 'dodged' : enemyDamaged ? 'damaged' : ''}>
                  <Sprite spriteID={NPC.id} animationDuration={0.5 + ((enemy.hp_max - enemyHP) / 10) * 2}/>
                </div>
                { cue && cue.text.length ? (
                  <Box sx={{ position: 'absolute', zIndex: 2, right: 0, top: '16px'}}>
                    <Cue log={cue}/>
                  </Box>
                ) : null }
              </Box>
              )
            : null}
          <Logs/>
          <Box sx={{marginY: '16px'}} className='options_wrapper'>
            <Options
              options={options}
              handleOption={handleOption}
              handleDefaultOption={handleDefaultOption}
              color='white'
              backgroundColor='black'
              borderColor='white'
              logCue=''
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'center', marginY: '16px' }} className='enemyStats'>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%'}}>
              { !navigation.loot_open && !navigation.enemy_dead && enemy && playerHP ? (
                <>
                  <Bar label='HP' currentValue={playerHP} maximumValue={player.hp_max} color='white'/>
                  <Bar label='AP' currentValue={playerAP} maximumValue={player.ap_max} color='white'/>
                </>
              )
                : null}
            </Box>
          </Box>
        </Box>
      </Box>
      
      )
}

export default React.memo(Combat)
